import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./TermsConditions.css"; // reuse the same CSS file

const GrievancePolicy = () => {
  return (
    <React.Fragment>
      <Header variant="light" />
      <div className="grievance-policy">
        <Helmet>
          <title>Grievance Policy - Panmitra Online Services</title>
          <meta
            name="description"
            content="Grievance Policy for Panmitra. Learn how to file a complaint, our redressal mechanism, and contact details of our Grievance Officer for PAN card, recharge, bill payment, and insurance services."
          />
        </Helmet>

        <div className="body-overlay"></div>

        <main>
          {/*===== HERO AREA STARTS =======*/}
          <div
            className="about-header-area"
            style={{
              backgroundImage: "url(assets/img/bg/inner-header.png)",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="container">
              <div className="row">
                <div className="col-lg-7 m-auto">
                  <div className="about-inner-header heading9 text-center">
                    <h1>Grievance Policy</h1>
                    <a href="/">
                      Home <i className="fa-solid fa-angle-right" />{" "}
                      <span>Grievance Policy</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Grievance Policy Content inside a Card ===== */}
          <div className="container py-5">
            <div className="terms-card p-5 bg-white rounded shadow-sm">
              <h1 className="fw-bold text-center mb-4">Grievance Policy</h1>
              <p className="text-center text-muted effective-date">
                Last Updated: 01.03.2026
              </p>

              <section className="mb-5">
                <h2>1. Introduction</h2>
                <p>
                  At <strong>Panmitra</strong> (operated by{" "}
                  <strong>PANMITRA ONLINE SERVICE PRIVATE LIMITED</strong>), we
                  are committed to providing the highest quality of service to
                  our agents, distributors, and customers. We understand that
                  sometimes things may not go as planned, and we are here to
                  listen and resolve your concerns. This Grievance Policy
                  outlines the mechanism for filing complaints and the process
                  we follow to address them promptly and fairly.
                </p>
                <p>
                  This policy is formulated in compliance with the Information
                  Technology Act, 2000, the Consumer Protection Act, 2019, and
                  other applicable laws. It applies to all users of our platform
                  and services, including but not limited to PAN card services,
                  mobile/DTH recharge, utility bill payments, motor insurance
                  (POSP), and API solutions.
                </p>
                <p>
                  Our registered office is located at: <br />
                  <strong>PANMITRA ONLINE SERVICE PRIVATE LIMITED</strong>
                  <br />
                  Belagavi, Karnataka - 590015, India.
                </p>
              </section>

              <section className="mb-5">
                <h2>2. What Constitutes a Grievance?</h2>
                <p>
                  A grievance may include, but is not limited to, the following:
                </p>
                <ul>
                  <li>Failed or delayed PAN card applications.</li>
                  <li>
                    Failed or delayed mobile/DTH recharges or bill payments.
                  </li>
                  <li>Incorrect amount debited from your account.</li>
                  <li>
                    Technical issues while using our platform or mobile app.
                  </li>
                  <li>Disputes regarding motor insurance policy issuance.</li>
                  <li>
                    Issues related to KYC verification or account management.
                  </li>
                  <li>API integration issues or service disruptions.</li>
                  <li>Unsatisfactory response from customer support.</li>
                  <li>
                    Any other concerns regarding our products or services.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>3. How to File a Grievance</h2>
                <p>
                  You can file a grievance through any of the following
                  channels:
                </p>
                <ul>
                  <li>
                    <strong>Email:</strong> Send a detailed complaint to{" "}
                    <a href="mailto:panmitra121@gmail.com">
                      panmitra121@gmail.com
                    </a>
                    .
                  </li>
                  <li>
                    <strong>Phone:</strong> Call our support team at{" "}
                    <strong>+91 9632-456-111</strong> (Monday–Saturday, 10:00 AM
                    to 6:00 PM).
                  </li>
                  <li>
                    <strong>In Writing:</strong> You may also send a physical
                    letter to our registered address mentioned above.
                  </li>
                </ul>
                <p>
                  To help us resolve your issue quickly, please include the
                  following information in your complaint:
                </p>
                <ul>
                  <li>
                    Your full name and contact details (mobile number, email).
                  </li>
                  <li>
                    Transaction ID (if applicable) and date of transaction.
                  </li>
                  <li>A clear description of the issue.</li>
                  <li>
                    Any supporting documents (screenshots, bank statements,
                    etc.).
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>4. Grievance Redressal Process</h2>
                <p>
                  Once we receive your grievance, we follow a structured process
                  to ensure timely resolution:
                </p>
                <ol>
                  <li>
                    <strong>Acknowledgment:</strong> You will receive an
                    acknowledgment of your complaint within{" "}
                    <strong>24 hours</strong> (on working days) via email or
                    SMS.
                  </li>
                  <li>
                    <strong>Investigation:</strong> Our grievance officer or
                    designated team will investigate the issue, which may
                    involve coordination with our technology team, banking
                    partners, government portals (NSDL/UTI), or service
                    providers.
                  </li>
                  <li>
                    <strong>Resolution:</strong> We aim to resolve all
                    grievances within <strong>7 working days</strong> from the
                    date of receipt. In complex cases requiring external
                    investigation, we will keep you informed of the progress and
                    expected timeline.
                  </li>
                  <li>
                    <strong>Closure:</strong> Once a resolution is reached, you
                    will be notified of the outcome. If you are not satisfied
                    with the resolution, you may escalate the matter as
                    described in Section 6.
                  </li>
                </ol>
              </section>

              <section className="mb-5">
                <h2>5. Grievance Officer Details</h2>
                <p>
                  In compliance with the Information Technology (Intermediary
                  Guidelines and Digital Media Ethics Code) Rules, 2021, and
                  other applicable laws, we have appointed a Grievance Officer
                  to address your concerns.
                </p>
                <div className="p-3 bg-light rounded mb-3">
                  <p className="mb-2">
                    <strong>Name:</strong> Mr. ADRUSHAPPA RAMAPPA RANOJI
                  </p>
                  <p className="mb-2">
                    <strong>Designation:</strong> Grievance Officer
                  </p>
                  <p className="mb-2">
                    <strong>Email:</strong>{" "}
                    <a href="mailto:panmitra121@gmail.com">
                      panmitra121@gmail.com
                    </a>
                  </p>
                  <p className="mb-2">
                    <strong>Phone:</strong> +91 9632-456-111 – available Monday
                    to Saturday, 10:00 AM to 6:00 PM
                  </p>
                  <p className="mb-0">
                    <strong>Postal Address:</strong>
                  </p>
                  <address className="mb-0">
                    PANMITRA ONLINE SERVICE PRIVATE LIMITED
                    <br />
                    Attn: Grievance Officer
                    <br />
                    Belagavi, Karnataka - 590015, India
                  </address>
                </div>
                <p className="text-muted small">
                  <em>
                    Note: The Grievance Officer will acknowledge receipt of any
                    written complaint within 24 hours and endeavor to resolve it
                    within 15 days.
                  </em>
                </p>
              </section>

              <section className="mb-5">
                <h2>6. Escalation Mechanism</h2>
                <p>
                  If you are not satisfied with the resolution provided by the
                  Grievance Officer, you may escalate your complaint to the
                  following:
                </p>
                <ul>
                  <li>
                    <strong>Nodal Officer:</strong> You may contact our Nodal
                    Officer at{" "}
                    <a href="mailto:panmitra121@gmail.com">
                      panmitra121@gmail.com
                    </a>{" "}
                    for a review of your case.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>7. Confidentiality</h2>
                <p>
                  All grievances and related information will be treated as
                  confidential and will only be used for the purpose of
                  resolving the issue. We will not disclose your personal
                  information to third parties except as required by law or with
                  your consent.
                </p>
              </section>

              <section className="mb-5">
                <h2>8. Amendments</h2>
                <p>
                  We reserve the right to amend this Grievance Policy at any
                  time. Any changes will be effective immediately upon posting
                  on this page. We encourage you to review this page
                  periodically to stay informed about how we handle grievances.
                </p>
              </section>

              <section className="mb-5">
                <h2>9. Contact Information</h2>
                <p>
                  For any queries regarding this policy or to file a grievance,
                  you can reach us at:
                </p>
                <address>
                  <strong>PANMITRA ONLINE SERVICE PRIVATE LIMITED</strong>
                  <br />
                  Belagavi, Karnataka - 590015, India
                  <br />
                  Phone: +91 9632-456-111
                  <br />
                  Email:{" "}
                  <a href="mailto:panmitra121@gmail.com">
                    panmitra121@gmail.com
                  </a>
                  <br />
                  Web: https://instantpanmitra.com
                </address>
              </section>

              <div className="text-center mt-5">
                <p className="mt-3 text-muted">
                  By using our services, you acknowledge that you have read and
                  understood this Grievance Policy.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default GrievancePolicy;
